// Location: src/features/error-monitor/components/ErrorEventsList.tsx

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  TablePagination,
  Checkbox,
} from '@mui/material';
import {
  DirectionsWalk as WalkInIcon,
} from '@mui/icons-material';
import {
  useTranslate,
  useDataProvider,
  useLocale,
  usePermissions,
  ListContextProvider,
  TextField,
  FunctionField,
} from 'react-admin';
import { format } from 'date-fns';

import type { DetailedErrorEvent, ErrorFilters, ErrorByDevice } from '../types';

import { CustomDataGrid } from '@/components/common';
import { EventMetadataPopover } from '@/features/events/components/EventMetadataPopover';
import type { UseBulkSelectionReturn } from '@/hooks/useBulkSelection';

type ViewMode = 'list' | 'grouped';

interface ErrorEventsListProps {
  filters: ErrorFilters;
  viewMode: ViewMode;
  metadataExpanded?: boolean;
  selection?: UseBulkSelectionReturn;
  reloadKey?: number;
}

interface GroupedError {
  id: string;
  deviceId: string;
  deviceName?: string | null;
  errorCount: number;
  lastErrorAt: string;
  lastErrorType: string;
  lastErrorCode?: string;
}

export const ErrorEventsList = ({ filters, viewMode, metadataExpanded = false, selection, reloadKey }: ErrorEventsListProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const locale = useLocale();
  const { permissions } = usePermissions();
  const isSuperAdmin = permissions === 'SuperAdmin' || permissions === 'Admin';

  const [events, setEvents] = useState<DetailedErrorEvent[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedError[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (viewMode === 'list') {
        // Use new detailed error events endpoint
        const result = await dataProvider.getDetailedErrorEvents(
          filters,
          page + 1,
          rowsPerPage,
          locale
        );

        setEvents(result.data.items);
        setTotal(result.data.totalItems);
      } else {
        // Grouped by device view: the same events as the list, with every filter applied
        const result = await dataProvider.getErrorsByDevice(filters);

        const grouped: GroupedError[] = result.data.map((d: ErrorByDevice, index: number) => ({
          id: `${d.deviceId}-${index}`,
          deviceId: d.deviceId,
          deviceName: d.deviceName,
          errorCount: d.errorCount,
          lastErrorAt: d.lastErrorAt,
          lastErrorType: d.deviceType,
          lastErrorCode: undefined,
        }));

        setGroupedData(grouped);
        setTotal(grouped.length);
      }
    } catch (err: any) {
      console.error('Error loading error events:', err);
      setError(err.message || translate('resources.errorMonitor.errors.loadEventsFailed'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, viewMode, locale, dataProvider, translate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload when parent triggers (after bulk delete)
  useEffect(() => {
    if (reloadKey && reloadKey > 0) {
      loadData();
    }
  }, [reloadKey]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // List View - Using CustomDatagrid with ListContextProvider
  if (viewMode === 'list') {
    // Create list context for CustomDatagrid
    const listContext = {
      data: events,
      total,
      isLoading: loading,
      page: page + 1,
      perPage: rowsPerPage,
      setPage: (p: number) => setPage(p - 1),
      setPerPage: setRowsPerPage,
      sort: { field: 'createdAt', order: 'DESC' as const },
      filterValues: filters,
      resource: 'error-events',
      selectedIds: [],
      onSelect: () => {},
      onToggleItem: () => {},
      onUnselectItems: () => {},
      refetch: loadData,
    };

    const visibleIds = events.map((e) => e.id);

    return (
      // Contained card (application-shell table pattern): the table scrolls
      // INSIDE this card on both axes and the pagination stays pinned below,
      // instead of the whole page growing past the viewport.
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 250px)',
          minHeight: 200,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: (t) => t.custom?.radius?.card ?? '10px',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ListContextProvider value={listContext as any}>
          <CustomDataGrid bulkActionButtons={false} sx={{ border: 'none', borderRadius: 0, height: '100%', overflow: 'auto' }}>
            {/* Selection checkbox (SuperAdmin only) */}
            {isSuperAdmin && selection && (
              <FunctionField
                label={
                  <Checkbox
                    size="small"
                    checked={selection.allSelected(visibleIds)}
                    indeterminate={selection.count > 0 && !selection.allSelected(visibleIds)}
                    onChange={() => selection.selectAll(visibleIds)}
                  />
                }
                render={(record: DetailedErrorEvent) => (
                  <Checkbox
                    size="small"
                    checked={selection.isSelected(record.id)}
                    onChange={() => selection.toggle(record.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              />
            )}

            {/* Timestamp */}
            <FunctionField
              label={translate('resources.errorMonitor.table.timestamp')}
              render={(record: DetailedErrorEvent) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {format(new Date(record.createdAt), 'dd/MM HH:mm:ss')}
                </Typography>
              )}
            />

            {/* Device */}
            <FunctionField
              label={translate('resources.errorMonitor.table.device')}
              render={(record: DetailedErrorEvent) => {
                const device = record.device;
                const hotel = record.hotel;
                const deviceDisplay = device
                  ? (device.name ? `${device.installationCode} - ${device.name}` : device.installationCode)
                  : (device?.deviceId || '-');

                return device ? (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2">{deviceDisplay}</Typography>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                          {device.deviceType} • {device.channel}
                        </Typography>
                        {hotel && (
                          <Typography variant="caption" display="block">
                            {hotel.hotelName}
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', cursor: 'help', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {deviceDisplay}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {deviceDisplay}
                  </Typography>
                );
              }}
            />

            {/* Group - Hotel */}
            <FunctionField
              label={`${translate('resources.errorMonitor.table.group')} - ${translate('resources.errorMonitor.table.hotel')}`}
              render={(record: DetailedErrorEvent) => {
                const hotel = record.hotel;
                if (!hotel) return <Typography variant="body2" color="text.secondary">-</Typography>;
                return (
                  <Box sx={{ maxWidth: 130 }}>
                    {hotel.groupName && (
                      <Typography variant="caption" display="block" color="text.secondary" lineHeight={1.2}>
                        {hotel.groupName}
                      </Typography>
                    )}
                    <Typography variant="body2" fontSize="0.8rem" lineHeight={1.2}>
                      {hotel.hotelName || hotel.hotelId}
                    </Typography>
                  </Box>
                );
              }}
            />

            {/* Error — severity dot + description, with nature/category consolidated
                here (their own columns were removed to fit the available width). */}
            <FunctionField
              label={translate('resources.errorMonitor.table.error')}
              render={(record: DetailedErrorEvent) => {
                const definition = record.errorDefinition;
                if (definition) {
                  const nature = definition.nature || 'Error';
                  const isError = definition.countsAsError !== false;
                  return (
                    <Box sx={{ minWidth: 320, display: 'flex', gap: 1 }}>
                      <Box
                        title={translate(`resources.errorDefinitions.severity.${definition.severity.toLowerCase()}`, { _: definition.severity })}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          mt: 0.6,
                          flex: 'none',
                          backgroundColor: definition.severityColor || '#9e9e9e',
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontSize="0.8rem" fontWeight={600} lineHeight={1.3}>
                          {definition.title}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" lineHeight={1.3}>
                          {definition.description}
                        </Typography>
                        {definition.recommendation && (
                          <Typography variant="caption" display="block" color="info.main" lineHeight={1.3} sx={{ fontStyle: 'italic', mt: 0.25 }}>
                            {definition.recommendation}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 0.9,
                              py: 0.15,
                              borderRadius: '999px',
                              fontSize: '0.66rem',
                              fontWeight: 600,
                              backgroundColor: isError ? '#F4433615' : nature === 'Operational' ? '#FF980015' : '#0288D115',
                              color: isError ? '#F44336' : nature === 'Operational' ? '#E65100' : '#0288D1',
                            }}
                          >
                            {translate(`resources.errorMonitor.nature.${nature.toLowerCase()}`, { _: nature })}
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.66rem' }}>
                            {translate(`resources.errorDefinitions.category.${definition.category.toLowerCase()}`, { _: definition.category })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                } else if (record.errorCode) {
                  return (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {record.errorCode}
                    </Typography>
                  );
                } else if (record.legacyDescription) {
                  return (
                    <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                      {record.legacyDescription}
                    </Typography>
                  );
                }
                return <Typography variant="body2" color="text.secondary">-</Typography>;
              }}
            />

            {/* Reference (reservation - room) */}
            <FunctionField
              label={translate('resources.errorMonitor.table.reference')}
              render={(record: DetailedErrorEvent) => {
                const journey = record.journey;
                const reservationNumber = journey?.reservationNumber || record.reservationNumber;
                const roomNumber = journey?.roomNumber || record.metadata?.roomNumber;
                const isWalkIn = journey?.isWalkIn || record.metadata?.isWalkingGuest === 'true';

                const parts = [reservationNumber, roomNumber].filter(Boolean);
                if (parts.length === 0) {
                  return <Typography variant="body2" color="text.secondary">-</Typography>;
                }

                return (
                  <Tooltip
                    title={
                      journey ? (
                        <Box>
                          <Typography variant="body2">
                            {translate('resources.errorMonitor.table.journeyInfo')}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {translate('resources.errorMonitor.table.journeyStatus')}: {journey.status}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {translate('resources.errorMonitor.table.journeyEvents')}: {journey.totalEventsInJourney}
                          </Typography>
                          {journey.errorsInJourney > 0 && (
                            <Typography variant="caption" display="block" color="error.main">
                              {translate('resources.errorMonitor.table.journeyErrors')}: {journey.errorsInJourney}
                            </Typography>
                          )}
                        </Box>
                      ) : ''
                    }
                    arrow
                    disableHoverListener={!journey}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 90 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: journey ? 'help' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {parts.join(' - ')}
                      </Typography>
                      {isWalkIn && (
                        <Tooltip title={translate('resources.errorMonitor.table.walkInGuest')} arrow>
                          <WalkInIcon sx={{ fontSize: 14, color: 'info.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Tooltip>
                );
              }}
            />

            {/* Metadata */}
            <FunctionField
              label={translate('resources.errorMonitor.table.metadata')}
              render={(record: DetailedErrorEvent) => (
                <Box sx={{ maxWidth: metadataExpanded ? 'none' : 150 }}>
                  <EventMetadataPopover
                    metadata={record.metadata}
                    eventCode={record.errorType}
                    errorCode={record.errorCode}
                    expanded={metadataExpanded}
                    excludeKeys={['reservationNumber', 'roomNumber', 'isWalkingGuest']}
                  />
                </Box>
              )}
            />
          </CustomDataGrid>
        </ListContextProvider>
        </Box>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count !== -1 ? count : `+${to}`}`
          }
          sx={{ flex: 'none', borderTop: '1px solid', borderColor: 'divider' }}
        />

      </Box>
    );
  }

  // Grouped by Device View
  const groupedListContext = {
    data: groupedData,
    total: groupedData.length,
    isLoading: loading,
    page: 1,
    perPage: 100,
    setPage: () => {},
    setPerPage: () => {},
    sort: { field: 'errorCount', order: 'DESC' as const },
    filterValues: filters,
    resource: 'error-events-grouped',
    selectedIds: [],
    onSelect: () => {},
    onToggleItem: () => {},
    onUnselectItems: () => {},
    refetch: loadData,
  };

  return (
    // Same contained-card scroll pattern as the list view.
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 250px)',
        minHeight: 200,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: (t) => t.custom?.radius?.card ?? '10px',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <ListContextProvider value={groupedListContext as any}>
        <CustomDataGrid bulkActionButtons={false} sx={{ border: 'none', borderRadius: 0, height: '100%', overflow: 'auto' }}>
          {/* Device */}
          <FunctionField
            label={translate('resources.errorMonitor.table.device')}
            render={(record: GroupedError) => (
              <Typography variant="body2">
                <Box component="span" sx={{ fontFamily: 'monospace' }}>{record.deviceId}</Box>
                {record.deviceName ? ` · ${record.deviceName}` : ''}
              </Typography>
            )}
          />

          {/* Error Count */}
          <FunctionField
            label={translate('resources.errorMonitor.table.errors')}
            render={(record: GroupedError) => (
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  color: record.errorCount > 10 ? 'error.main' : record.errorCount > 5 ? 'warning.main' : 'text.primary',
                }}
              >
                {record.errorCount}
              </Typography>
            )}
          />

          {/* Last Error */}
          <FunctionField
            label={translate('resources.errorMonitor.table.lastError')}
            render={(record: GroupedError) => (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {format(new Date(record.lastErrorAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            )}
          />
        </CustomDataGrid>
      </ListContextProvider>
      </Box>
    </Box>
  );
};

export default ErrorEventsList;
